/**
 * Recording Lifecycle Events
 *
 * Provides event-based communication for recording lifecycle changes.
 * Allows decoupling of idle stream management from recording logic.
 *
 * Events:
 * - recording-lifecycle: Emitted when recording state changes
 */

import { emit, listen, type UnlistenFn } from '@tauri-apps/api/event';

// The Tauri guard reads the injected seam: `isTauri` lived in
// `@/tauri/utils`, a runtime re-export of `@heidi/runtime/platform`, which is
// `type:runtime` — a band `type:bridge` may not depend on. See
// `PluginCoreConfig.isTauri` for why it is injected rather than recomputed here.
import { pluginCoreIsTauri, pluginCoreLogger } from '../config';

/** Recording lifecycle event types */
export type RecordingLifecycleEvent =
  | 'recording-starting' // About to start recording (idle stream should stop)
  | 'recording-started' // Recording has started
  | 'recording-stopping' // About to stop recording
  | 'recording-stopped'; // Recording stopped, cleanup complete (idle stream can restart)

/** Payload for recording lifecycle events */
export interface RecordingLifecyclePayload {
  /** The event type */
  event: RecordingLifecycleEvent;
  /** Timestamp when event was emitted */
  timestamp: number;
  /** Optional session ID for context */
  sessionId?: string;
}

const EVENT_NAME = 'recording-lifecycle';

/**
 * Emit a recording lifecycle event
 *
 * @param event - The lifecycle event type
 * @param sessionId - Optional session ID for context
 */
export async function emitRecordingLifecycleEvent(
  event: RecordingLifecycleEvent,
  sessionId?: string
): Promise<void> {
  if (!pluginCoreIsTauri()) return;

  const payload: RecordingLifecyclePayload = {
    event,
    timestamp: Date.now(),
    sessionId,
  };

  // Log state transitions clearly
  const transitionMap: Record<RecordingLifecycleEvent, string> = {
    'recording-starting': '>>> TRANSITION: IDLE -> RECORDING (starting)',
    'recording-started': '>>> TRANSITION: RECORDING active',
    'recording-stopping': '>>> TRANSITION: RECORDING -> IDLE (stopping)',
    'recording-stopped': '>>> TRANSITION: IDLE active (recording stopped)',
  };

  pluginCoreLogger().info(`[recording-lifecycle] ${transitionMap[event]}`, {
    event,
    sessionId,
  });
  await emit(EVENT_NAME, payload);
}

/**
 * Listen to recording lifecycle events
 *
 * @param callback - Function called when event is received
 * @returns Unlisten function to stop listening
 */
export async function listenToRecordingLifecycle(
  callback: (payload: RecordingLifecyclePayload) => void
): Promise<UnlistenFn> {
  if (!pluginCoreIsTauri()) {
    return () => {};
  }

  pluginCoreLogger().debug('[recording-lifecycle] Setting up listener');
  return listen<RecordingLifecyclePayload>(EVENT_NAME, (event) => {
    const { event: lifecycleEvent, sessionId } = event.payload;
    pluginCoreLogger().info(`[recording-lifecycle] <<< RECEIVED: ${lifecycleEvent}`, {
      sessionId,
    });
    callback(event.payload);
  });
}

/**
 * Recording lifecycle event emitter helpers
 * Use these instead of direct calls for cleaner code
 */
export const recordingLifecycle = {
  /** Emit when recording is about to start (idle stream should stop) */
  emitStarting: (sessionId?: string) =>
    emitRecordingLifecycleEvent('recording-starting', sessionId),

  /** Emit when recording has started */
  emitStarted: (sessionId?: string) =>
    emitRecordingLifecycleEvent('recording-started', sessionId),

  /** Emit when recording is about to stop */
  emitStopping: (sessionId?: string) =>
    emitRecordingLifecycleEvent('recording-stopping', sessionId),

  /** Emit when recording stopped and cleanup is complete (idle stream can restart) */
  emitStopped: (sessionId?: string) =>
    emitRecordingLifecycleEvent('recording-stopped', sessionId),
};
